import React, { useEffect, useRef, useState, useCallback, memo } from 'react';
import { logger } from '@/lib/logger';
import { optimizeSvgStrokes } from '@/utils/svgOptimizer';

interface FloorPlanCanvasProps {
  imageUrl: string;
  svgUrl?: string; // Optional SVG URL for CAD files
  onLoad?: (size: { width: number; height: number }) => void;
  isSimulationRunning?: boolean; // Add prop to know if simulation is running
  grayscale?: boolean; // Add prop for grayscale effect
  opacity?: number; // Image opacity (0-100), defaults to 100
}

const FloorPlanCanvasComponent: React.FC<FloorPlanCanvasProps> = ({
  imageUrl,
  svgUrl,
  onLoad,
  isSimulationRunning = false,
  grayscale = false,
  opacity = 100,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [optimizedSvgUrl, setOptimizedSvgUrl] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const hasCalledOnLoad = useRef(false);

  // Use optimized SVG URL if available, then SVG URL, then regular image URL
  const displayUrl = optimizedSvgUrl || svgUrl || imageUrl;
  const isSvg = !!svgUrl;

  const handleImageLoad = useCallback(() => {
    const img = imgRef.current;
    if (img && onLoad && !hasCalledOnLoad.current) {
      hasCalledOnLoad.current = true;
      const dimensions = { width: img.naturalWidth, height: img.naturalHeight };
      logger.log('FloorPlanCanvas - Image loaded:', {
        naturalDimensions: `${dimensions.width}x${dimensions.height}`,
        srcType: isSvg ? 'SVG' : 'Raster',
        displayUrl: displayUrl?.substring(0, 100),
      });
      onLoad(dimensions);
    }
    setIsLoading(false);
    setError(null);
  }, [onLoad, isSvg, displayUrl]);

  useEffect(() => {
    // Reset the flag when URL changes
    hasCalledOnLoad.current = false;
    setIsLoading(true);
    setError(null);
    setOptimizedSvgUrl(null);

    if (!imageUrl && !svgUrl) {
      setError('No image URL provided');
      setIsLoading(false);
      return;
    }

    logger.log(`Loading ${isSvg ? 'SVG' : 'image'} from:`, svgUrl || imageUrl);

    const loadImage = async () => {
      try {
        if (isSvg && svgUrl) {
          // For SVG files, fetch and optimize them first
          logger.log('Loading and optimizing SVG file from:', svgUrl);

          try {
            const response = await fetch(svgUrl);
            if (response.ok) {
              const svgText = await response.text();
              logger.log('SVG fetched, optimizing strokes...');

              // Optimize the SVG strokes
              const optimizedSvg = optimizeSvgStrokes(svgText);

              // Create a blob URL from the optimized SVG
              const blob = new Blob([optimizedSvg], { type: 'image/svg+xml' });
              const blobUrl = URL.createObjectURL(blob);

              setOptimizedSvgUrl(blobUrl);
              logger.log('SVG optimized and blob URL created');

              // Clean up blob URL when component unmounts or URL changes
              return () => URL.revokeObjectURL(blobUrl);
            }
          } catch (err) {
            logger.log('Failed to fetch/optimize SVG, will use original URL:', err);
            // Continue with original URL if optimization fails
          }
        }

        // Preload the image (works for both SVG and raster images)
        const img = new Image();
        img.crossOrigin = 'anonymous'; // Add CORS support

        img.onload = () => {
          logger.log(`${isSvg ? 'SVG' : 'Image'} loaded successfully:`, displayUrl);
          logger.log('Image dimensions:', { width: img.naturalWidth, height: img.naturalHeight });
          setIsLoading(false);
          setError(null);
        };

        img.onerror = e => {
          logger.error(`Failed to load floor plan ${isSvg ? 'SVG' : 'image'}:`, displayUrl, e);

          // If SVG fails, try to fallback to regular image URL
          if (isSvg && svgUrl && img.src === svgUrl && imageUrl) {
            logger.log('SVG failed to load, falling back to PNG');
            img.src = imageUrl;
            return;
          }

          // Try to fetch the image to get more error details
          fetch(displayUrl)
            .then(async response => {
              logger.log('Fetch response:', response.status, response.statusText);
              logger.log('Response headers:', {
                contentType: response.headers.get('content-type'),
                contentLength: response.headers.get('content-length'),
                accessControl: response.headers.get('access-control-allow-origin'),
              });

              // Since it's returning JSON, let's read the error message
              if (response.headers.get('content-type')?.includes('application/json')) {
                const errorData = await response.json();
                logger.error('Storage error details:', errorData);
              }
            })
            .catch(fetchError => {
              logger.error('Fetch error:', fetchError);
            });

          setIsLoading(false);
          setError(`Failed to load floor plan ${isSvg ? 'SVG' : 'image'}`);
        };

        img.src = displayUrl;
      } catch (error) {
        logger.error('Error in loadImage:', error);
        setIsLoading(false);
        setError('Failed to load floor plan');
      }
    };

    loadImage();

    // Cleanup function
    return () => {
      if (optimizedSvgUrl) {
        URL.revokeObjectURL(optimizedSvgUrl);
      }
    };
  }, [svgUrl, imageUrl]); // Remove displayUrl from deps to avoid infinite loop

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-8 bg-red-50 text-red-600">
        <p>{error}</p>
        <p className="text-xs mt-2 text-red-500">
          Try refreshing the page. If the problem persists, contact support.
        </p>
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%', position: 'relative' }}>
      {isLoading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        </div>
      )}
      <img
        ref={imgRef}
        src={displayUrl}
        alt="Floor Plan"
        className={`max-w-none ${isLoading ? 'hidden' : ''}`}
        style={{
          pointerEvents: 'none',
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          // Ensure SVG renders crisply
          imageRendering: isSvg ? 'crisp-edges' : 'auto',
          display: 'block',
          // Apply filters based on state (brightness removed - unified overlay handles dimming)
          filter: grayscale ? 'grayscale(100%)' : 'none',
          // Apply opacity (convert from 0-100 to 0-1)
          opacity: opacity / 100,
          transform: 'translateZ(0)',
        }}
        onLoad={handleImageLoad}
        crossOrigin="anonymous"
        onError={e => {
          // If SVG fails to load, fall back to PNG
          if (isSvg && svgUrl && e.currentTarget.src === svgUrl && imageUrl) {
            logger.log('SVG failed to load in img tag, falling back to PNG');
            e.currentTarget.src = imageUrl;
          }
        }}
      />
    </div>
  );
};

export const FloorPlanCanvas = memo(FloorPlanCanvasComponent);
